// Order progress
// This view tracks order progress by adding the 'order-section--complete' class
// to order section elements as the user completes them. This view also handles
// 'promptProgress' events (emitted by the order.checkout-button
// view) which provides indications to the user about any uncompleted sections
// of their order.
import i18n from 'i18next';
import _ from 'lodash';

const { UI, Order, Customer, headoffice } = Mobi2Go;

UI.View({
    name: 'order_sections',
    model: 'Order',
    el: '[data-view="order-sections"]',
    initialise() {
        UI.bind('promptProgress', this.warning);
        UI.bind('progressForward', this.progressForward);
        this.$el.localize();
    },
    sections: {
        customer: new UI.OrderSection('[data-order-section="customer"]'),
        location: new UI.OrderSection('[data-order-section="location"]'),
        method: new UI.OrderSection('[data-order-section="method"]'),
        address: new UI.OrderSection('[data-order-section="address"]'),
        expected_at: new UI.OrderSection('[data-order-section="expected-at"]'),
        basket: new UI.OrderSection('[data-order-section="basket"]'),
    },
    fadeSections(fade, except_section) {
        _.each(this.sections, (section, section_name) => {
            if (section_name === except_section) {
                section.unfade();
            } else {
                section.fade(fade);
            }
        });
    },

    model_listeners: {
        // Customer setion is complete when customer has logged in
        updateCustomer(customer) {
            if (
                !customer ||
                (Order.getMethod() === 'delivery' && !Order.getAddress())
            ) {
                return;
            }
            this.sections.customer.complete(true);
        },
        // Location section is complete when an order method is set
        // Set customer section as incomplete if order method is delivery and
        // an address is not set.
        updateMethod(order_method) {
            this.sections.location.complete(!!order_method);

            if (order_method === 'delivery' && !Order.getAddress()) {
                this.sections.customer.complete(false);
            } else if (Order.getCustomer()) {
                this.sections.customer.complete(true);
            }
        },
        // Customer section is complete when an address is set
        updateAddress(address) {
            if (Order.getMethod() === 'delivery') {
                this.sections.customer.complete(!!address);
            }
        },
        // Basket section is complete when the basket has at least one product.
        updateProductsQuantity(count) {
            this.sections.basket.complete(!!count);
        },
    },

    progressForward(from) {
        switch (from) {
            case 'main_page':
                // CB 4/2/21 - SHow verification screen if the customer isn't verified and tries to progress

                if (Customer.shouldPromptSMSVerification()) {
                    Order.log('Unverified customer tried to check out');
                    UI.trigger('showCustomerVerification');
                    return;
                }

                if (!Order.isConfirmable()) {
                    UI.trigger('promptProgress');
                    return;
                }

                const product_with_unmet_modifier_requirements = Order.getFirstProductWithUnmetModifierRequirements();
                if (product_with_unmet_modifier_requirements) {
                    UI.trigger(
                        'editProduct',
                        product_with_unmet_modifier_requirements
                    );
                    const {
                        menu_product,
                    } = product_with_unmet_modifier_requirements;
                    Order.log(
                        'checkout button clicked with product with unmet modifier selections',
                        {
                            product_id: menu_product.id,
                            product_name: menu_product.name,
                        }
                    );
                    return;
                }

                if (Order.shouldShowPreconfirmationWarning()) {
                    UI.trigger('showPreconfirmationWarning');
                    return;
                }

                if (Order.shouldPromptForTip()) {
                    UI.trigger('showTip');
                    return;
                }

                if (Order.shouldPromptForGifting()) {
                    UI.trigger('showGifting');
                    return;
                }

                if (!Customer.getPhoneNumber()) {
                    Customer.trigger('requestPhoneNumber');
                }

                UI.trigger('showConfirmation');
                break;

            case 'preconfirmation_warning':
                if (Order.shouldPromptForTip()) {
                    UI.trigger('showTip');
                    return;
                }

                if (Order.shouldPromptForGifting()) {
                    UI.trigger('showGifting');
                    return;
                }

                UI.trigger('showConfirmation');
                break;

            case 'tip':
                if (Order.shouldPromptForGifting()) {
                    UI.trigger('showGifting');
                    return;
                }

                UI.trigger('showConfirmation');
                break;

            case 'gifting':
                UI.trigger('showConfirmation');
                break;

            default:
                UI.trigger('showConfirmation');
        }
    },

    // Indicates to the user that a section has not been completed
    warning(message) {
        const error_id =
            message || Order.getProgressConfirmabilityStatus().getErrorId();

        if (UI.Modal.isOpen()) {
            return;
        }

        switch (error_id) {
            case 'customer required':
                UI.helpers.scrollTo(
                    this.sections.customer.$element,
                    () => {
                        UI.alert({
                            id: 'customer-required',
                            title: i18n.t(
                                Mobi2Go.headoffice
                                    .customer_guest_checkout_enabled
                                    ? 'order.progress:customer_required_warning_guest_mode_title'
                                    : 'order.progress:customer_required_warning_title'
                            ),
                            body: i18n.t(
                                Mobi2Go.headoffice
                                    .customer_guest_checkout_enabled
                                    ? 'order.progress:customer_required_warning_guest_mode_body'
                                    : 'order.progress:customer_required_warning_body'
                            ),
                            temp: true,
                            primary_button: null,
                            el: this.sections.customer.$element,
                            onClose: this.fadeSections,
                        });
                        this.fadeSections(true, 'customer');
                        this.sections.customer.animateTitle();
                    },
                    false,
                    -20
                );
                break;

            case 'location required':
                UI.helpers.scrollTo(
                    this.sections.location.$element,
                    () => {
                        UI.alert({
                            id: 'location-required',
                            title: i18n.t(
                                'order.progress:location_required_warning_title'
                            ),
                            body: i18n.t(
                                'order.progress:location_required_warning_body'
                            ),
                            temp: true,
                            primary_button: false,
                            onClose: () => {
                                if (!Order.getLocation()) {
                                    /**
                                     * No need to show the location finder if the location was set in between
                                     * the opening and closure of this alert.
                                     */
                                    Mobi2Go.UI.trigger('showLocationsFinder');
                                }
                                this.fadeSections();
                            },
                            el: this.sections.location.$element.find(
                                '.m2g-order-location-button'
                            ),
                        });
                        this.fadeSections(true, 'location');
                        this.sections.location.animateTitle();
                    },
                    false,
                    -36
                );
                break;

            case 'method required':
                {
                    const order_methods_string = UI.views.order_method.orderMethodsString();
                    UI.helpers.scrollTo(this.sections.location.$element, () => {
                        UI.alert({
                            // Ugh..
                            id: 'method-required',
                            title: i18n.t(
                                'order.progress:method_required_warning_title',
                                { order_methods: order_methods_string }
                            ),
                            body: i18n.t(
                                'order.progress:method_required_warning_body',
                                { order_methods: order_methods_string }
                            ),
                            temp: true,
                            primary_button: null,
                            el: '[data-view="order-method"]',
                            onClose: this.fadeSections,
                        });
                        this.fadeSections(true, 'method');
                    });
                }
                break;

            case 'expected_at required':
                UI.helpers.scrollTo(this.sections.location.$element, () => {
                    this.fadeSections(true, 'expected_at');
                });
                break;

            case 'address required':
                UI.helpers.scrollTo(
                    this.sections.address.$element,
                    () => {
                        UI.alert({
                            title: i18n.t(
                                'order.progress:address_required_warning_title'
                            ),
                            body: i18n.t(
                                'order.progress:address_required_warning_body'
                            ),
                            temp: true,
                            primary_button: i18n.t(
                                'order.progress:address_required_warning_primary_button'
                            ),
                            onPrimaryButtonClick() {
                                Mobi2Go.UI.trigger('showCustomerAddresses');
                            },
                            el: '[data-order-delivery-address]',
                            onClose: this.fadeSections,
                        });
                        this.fadeSections(true, 'address');
                        this.sections.customer.animateTitle();
                    },
                    false,
                    -56
                );
                break;

            case 'product required':
                UI.helpers.scrollTo(
                    this.sections.basket.$element,
                    () => {
                        UI.alert({
                            id: 'order-empty',
                            title: i18n.t(
                                'order.progress:product_required_warning_title'
                            ),
                            body: i18n.t(
                                'order.progress:product_required_warning_body'
                            ),
                            temp: true,
                            primary_button: null,
                            el: this.sections.basket.$element,
                            onClose: this.fadeSections,
                        });
                        this.fadeSections(true, 'basket');
                        this.sections.basket.animateTitle();
                    },
                    false,
                    -15
                );
                break;

            case 'delivery minimum not met':
                UI.helpers.scrollTo(
                    this.sections.basket.$element,
                    () => {
                        UI.alert({
                            title: i18n.t(
                                'order.progress:delivery_minimum_warning_title',
                                {
                                    delivery_minimum: i18n.t('common:price', {
                                        price: Order.getDeliveryMinimumTotal(),
                                    }),
                                }
                            ),
                            body: i18n.t(
                                'order.progress:delivery_minimum_warning_body'
                            ),
                            temp: true,
                            primary_button: null,
                            el: this.sections.basket.$element.find(
                                '[data-view="order-total"]'
                            ),
                            onClose: this.fadeSections,
                        });
                        this.fadeSections(true, 'basket');
                        this.sections.basket.animateTitle();
                    },
                    false,
                    -15
                );
                break;
            default: {
                const confirmability_status = Order.getProgressConfirmabilityStatus();
                const error_title = confirmability_status.getErrorTitle();
                const error_message = confirmability_status.getErrorMessage();

                UI.centeredAlert({
                    id: error_id,
                    title: error_title,
                    body: error_message,
                    close_button: false,
                });
            }
        }
    },
});
